<script setup lang="ts">
import { Skeleton } from '@/ui/skeleton'

const props = withDefaults(
  defineProps<{
    page?: string
    hasHeader?: boolean
  }>(),
  {
    page: null,
    hasHeader: true
  }
)

const isCustomerDetail = computed(() => props.page === 'CustomerDetail')
const hasPageHeader = computed(() => props.hasHeader && !isCustomerDetail.value && props.page !== 'UsersListPage')
</script>
<template>
  <div>
    <div
      v-if="hasPageHeader"
      class="flex flex-col gap-4 px-6 pb-6 pt-6 sm:px-12 sm:pb-8 sm:pt-8 lg:flex-row lg:items-end lg:justify-between"
    >
      <Skeleton class="h-9 w-64 md:w-80" />
    </div>

    <div v-if="isCustomerDetail">
      <div class="w-full px-6 pt-6 sm:px-12">
        <Skeleton v-if="hasHeader" class="mb-2 h-4 w-28" />
        <div class="flex items-center gap-4">
          <Skeleton class="hidden size-20 shrink-0 rounded-full sm:block" />
          <Skeleton class="h-8 w-48 lg:w-80" />
        </div>
      </div>

      <div class="mx-auto mt-8 grid w-full grid-cols-12 gap-6 px-6 sm:px-12">
        <div class="col-span-12 lg:col-start-1">
          <div class="mt-6 border bg-white sm:rounded-lg">
            <div class="flex justify-between px-4 sm:px-6">
              <div class="flex items-center">
                <div class="relative mr-2 inline-flex h-12 w-12 overflow-hidden rounded-lg bg-stone-200 p-3" />
                <div class="px-4 py-5 sm:px-6">
                  <h2
                    id="applicant-information-title"
                    placeholder-animation
                    class="relative ml-2 h-5 w-36 overflow-hidden bg-stone-200"
                  />
                  <div placeholder-animation class="relative ml-2 mt-1 h-5 w-48 overflow-hidden bg-stone-200 lg:w-80" />
                </div>
              </div>
            </div>
            <div class="border-t">
              <div class="px-4 py-5 sm:px-6">
                <h2 placeholder-animation class="relative ml-2 h-5 w-36 overflow-hidden bg-stone-200" />
                <div class="mt-4 flex items-center justify-between">
                  <div placeholder-animation class="relative ml-2 h-8 w-64 overflow-hidden bg-stone-200" />
                  <div placeholder-animation class="relative ml-2 h-8 w-64 overflow-hidden bg-stone-200" />
                  <div placeholder-animation class="relative ml-2 h-8 w-64 overflow-hidden bg-stone-200" />
                  <div placeholder-animation class="w-22 relative ml-2 h-5 w-24 overflow-hidden bg-muted" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-span-12 space-y-6 lg:col-span-7">
          <div v-for="i in 2" :key="i" class="border bg-white sm:rounded-lg">
            <div class="ml-6 flex items-center">
              <div class="relative mr-2 inline-flex h-12 w-12 overflow-hidden rounded-lg bg-stone-200 p-3" />
              <div class="px-4 py-5 sm:px-6">
                <h2
                  id="applicant-information-title"
                  placeholder-animation
                  class="relative ml-2 h-5 w-36 overflow-hidden bg-stone-200"
                />
                <div placeholder-animation class="relative ml-2 mt-1 h-5 w-48 overflow-hidden bg-stone-200 lg:w-80" />
              </div>
            </div>
            <div class="border-t px-4 py-5 sm:px-6">
              <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 2xl:grid-cols-4">
                <div v-for="j in 4" :key="j" class="sm:col-span-1">
                  <dt placeholder-animation class="relative h-5 w-24 overflow-hidden bg-stone-200" />
                  <dd placeholder-animation class="relative mt-1 h-5 w-36 overflow-hidden bg-muted" />
                </div>
              </dl>
            </div>
          </div>
        </div>
        <section aria-labelledby="timeline-title" class="col-span-12 lg:col-span-5">
          <div class="border bg-white px-4 pb-5 sm:rounded-lg sm:px-6">
            <div class="flex justify-between">
              <div class="flex items-center">
                <div class="relative mr-2 inline-flex h-12 w-12 overflow-hidden rounded-lg bg-stone-200 p-3" />
                <div class="px-4 py-5 sm:px-6">
                  <h2
                    id="applicant-information-title"
                    placeholder-animation
                    class="relative ml-2 h-5 w-36 overflow-hidden bg-stone-200"
                  />
                  <div placeholder-animation class="relative ml-2 mt-1 h-5 w-48 overflow-hidden bg-stone-200 lg:w-80" />
                </div>
              </div>
            </div>
            <div class="flow-root max-h-screen overflow-y-auto border-t pr-2 pt-6">
              <ul class="overflow-hidden">
                <li v-for="i in 6" :key="i">
                  <div class="relative pb-8">
                    <span class="absolute left-4 top-4 -ml-px h-full w-0.5 bg-stone-200" aria-hidden="true" />
                    <div class="relative flex space-x-3">
                      <div class="relative mr-2 inline-flex h-8 w-8 overflow-hidden rounded-full bg-stone-200 p-3" />
                      <div class="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                        <div placeholder-animation class="relative ml-2 h-5 w-36 overflow-hidden bg-stone-200" />
                        <div placeholder-animation class="relative ml-2 h-5 w-20 overflow-hidden bg-muted" />
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
    <!--
      Default
    -->
    <div v-else class="px-6 pb-6 sm:px-12 sm:pb-12" :class="!hasPageHeader && 'pt-6 sm:pt-8'">
      <Skeleton class="h-16 w-full" />
      <Skeleton class="mt-6 h-96 w-full" />
    </div>
  </div>
</template>

<style>
[placeholder-animation]::after {
  content: ' ';
  box-shadow: 0 0 50px 9px rgba(254, 254, 254, 1);
  position: absolute;
  top: 0;
  left: -100%;
  height: 100%;
  animation: load 1s infinite;
}
</style>
